import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { useTabParam, PosterImage } from '@structural-climbing/ui'
import { ChipTabs } from '../design/ChipTabs'
import { useMemberCheckInStatus } from '../features/checkin/useMemberCheckInStatus'
import { CompetitionIntro } from '../features/competitions/CompetitionIntro'
import { CompetitionNotStarted } from '../features/competitions/CompetitionNotStarted'
import { CompetitionResults } from '../features/competitions/CompetitionResults'
import { CompetitionRoutesTab } from '../features/competitions/CompetitionRoutesTab'
import { CompetitionStandingsTab } from '../features/competitions/CompetitionStandingsTab'
import { CompetitionScorecardTab } from '../features/competitions/CompetitionScorecardTab'
import { Page } from '../design/Page'
import { SurfaceCard } from '../design/SurfaceCard'
import { EmptyState } from '../design/EmptyState'
import { SkeletonCard } from '../design/Skeleton'
import { GenderRequiredModal } from '../features/competitions/GenderRequiredModal'
import { useCompetition } from '../features/competitions/useCompetition'
import { formatWallClockTime } from '../features/competitions/wallClock'
import { useMyGyms } from '../features/gym/useMyGyms'
import { markCompetitionJoined } from '../features/competitions/markCompetitionJoined'

const COMPETITION_TABS = ['standings', 'scorecard', 'routes'] as const

export function CompetitionPage() {
  // The URL carries the slug now. The server resolves either, so an old link
  // holding an id keeps working.
  const { competitionId } = useParams<{ competitionId: string }>()
  const { getAccessTokenSilently, user } = useAuth()
  // Needed to patch the same cache key the competitions list is stored under.
  const { gyms: myGyms } = useMyGyms()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const { competition, loading, notFound, reload } = useCompetition(
    competitionId,
    apiUrl,
    getAccessTokenSilently,
  )

  // Everything gym-shaped comes from the competition itself: this page no
  // longer sits under a layout that could supply it.
  const gymId = competition?.gym_id ?? null
  const hasActiveSubscription = competition?.has_active_subscription ?? false

  // Mirrors the Routes page: a member who used their last entry to check in has
  // a depleted subscription but is standing in the gym.
  const { isCheckedIn } = useMemberCheckInStatus(
    user?.sub as string | undefined,
    gymId,
    apiUrl,
    getAccessTokenSilently,
  )

  /**
   * The desk confirms from the gym app, and this page would otherwise hold the
   * snapshot it loaded with until the competitor thought to refresh.
   *
   * Polled rather than subscribed: competition_entries is Worker-fronted with a
   * service-role policy and is not in the realtime publication, so unlike
   * join_requests there is nothing a member's own token could listen to. Bounded
   * to the one state that is waiting for someone else to act, and it stops the
   * moment they are confirmed.
   */
  const awaitingConfirmation = competition?.awaiting_confirmation ?? false
  useEffect(() => {
    if (!awaitingConfirmation) return
    const timer = setInterval(() => void reload(), 15_000)
    // The common case is walking back to the app after the desk waved you
    // through, which deserves an answer faster than the next tick.
    const onFocus = () => void reload()
    window.addEventListener('focus', onFocus)
    return () => {
      clearInterval(timer)
      window.removeEventListener('focus', onFocus)
    }
  }, [awaitingConfirmation, reload])

  const [joining, setJoining] = useState(false)
  const [joinError, setJoinError] = useState<string | null>(null)
  const [genderModalOpen, setGenderModalOpen] = useState(false)
  const [standingsVersion, setStandingsVersion] = useState(0)
  // Called unconditionally: the strip below only renders on one branch, and a
  // ?tab= on the others is simply inert.
  const [tab, setTab] = useTabParam('tab', COMPETITION_TABS, 'standings')

  async function handleJoin(categoryId: string, sessionId: string | null) {
    // Explained up front, and enforced again by the 409 below.
    if (competition?.join_blocked_reason === 'gender') {
      setGenderModalOpen(true)
      return
    }
    setJoining(true)
    setJoinError(null)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/my-competitions/${competitionId}/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, category_id: categoryId }),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { code?: string }
        if (body.code === 'PROFILE_GENDER_REQUIRED') setGenderModalOpen(true)
        else if (body.code === 'PROFILE_PRIVATE') await reload()
        else if (body.code === 'PROFILE_DOB_REQUIRED')
          setJoinError(t('competitions.errorDobRequired'))
        else if (body.code === 'SESSION_FULL') setJoinError(t('competitions.errorSessionFull'))
        else if (body.code === 'CATEGORY_FULL') setJoinError(t('competitions.errorCategoryFull'))
        else if (body.code === 'MEMBERS_ONLY') setJoinError(t('competitions.errorMembersOnly'))
        else if (body.code === 'SESSION_REQUIRED')
          setJoinError(t('competitions.errorSessionRequired'))
        else setJoinError(t('competitions.errorJoinFailed'))
        return
      }
      await reload()
      if (competition)
        markCompetitionJoined(
          competition.id,
          competition.gym_id,
          myGyms.map((g) => g.id),
          true,
        )
    } catch {
      setJoinError(t('competitions.errorJoinFailed'))
    } finally {
      setJoining(false)
    }
  }

  if (loading) {
    return (
      <Page width="wide">
        <SkeletonCard lines={3} />
        <SkeletonCard lines={4} />
      </Page>
    )
  }

  if (notFound || !competition) {
    return (
      <Page width="wide">
        <EmptyState icon="🏆" title={t('competitions.notFound')} />
      </Page>
    )
  }

  const mySession = competition.my_entry?.session_id
    ? competition.sessions.find((s) => s.id === competition.my_entry?.session_id)
    : null
  const groupClosed =
    competition.my_session_state === 'ended' || competition.my_session_state === 'upcoming'

  return (
    <Page width="wide">
      <div>
        <div className="flex items-center gap-3">
          <Link to="/competitions" className="text-sm text-primary">
            ← {t('competitions.backToList')}
          </Link>
        </div>
        <h1 className="mt-1 text-2xl font-semibold">{competition.name}</h1>
        <p className="text-sm text-foreground/60">
          {competition.gym_name ? `${competition.gym_name} · ` : ''}
          {t(`competitions.state_${competition.state}`)} ·{' '}
          {t('competitions.competitorCount', { count: competition.entry_count })}
        </p>
      </div>

      {/* The whole poster, never cropped: the gym designed it, and the name and
          date usually sit right at its edges. Tap to see it full screen. */}
      {competition.poster_url && (
        <div className="overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
          <PosterImage
            src={competition.poster_url}
            alt={competition.name}
            fit="natural"
            maxHeight="60vh"
            zoomable="image"
            labels={{
              open: t('competitions.posterOpen'),
              close: t('competitions.posterClose'),
              zoomHint: t('competitions.posterZoomHint'),
            }}
          />
        </div>
      )}

      {/* Said out loud, so a competitor knows what they are waiting for rather
          than finding their first top silently refused. */}
      {competition.awaiting_confirmation && (
        <SurfaceCard className="flex flex-col gap-1 border-warning/40 bg-warning/5 p-4">
          <p className="text-sm font-medium text-warning">
            {t('competitions.awaitingConfirmation')}
          </p>
          <p className="text-xs text-foreground/60">{t('competitions.awaitingConfirmationHint')}</p>
        </SurfaceCard>
      )}

      {competition.state === 'ended' ? (
        // Finished: the scoreboard is a record now, and it is worth seeing
        // whether or not this member took part.
        <CompetitionResults
          competition={competition}
          myUserId={user?.sub}
          apiUrl={apiUrl}
          getAccessTokenSilently={getAccessTokenSilently}
        />
      ) : competition.joined && competition.state === 'upcoming' ? (
        // Joined, but nothing to score yet — the state comes from the server,
        // which computes it against the gym's own clock, not the viewer's.
        <CompetitionNotStarted competition={competition} />
      ) : competition.joined ? (
        <div className="flex flex-col gap-4">
          <ChipTabs
            aria-label={competition.name}
            value={tab}
            onChange={setTab}
            options={[
              { key: 'standings', label: t('competitions.tabStandings') },
              { key: 'scorecard', label: t('scorecard.tab') },
              { key: 'routes', label: t('competitions.tabRoutes') },
            ]}
          />
          <div role="tabpanel" aria-label={competition.name}>
            {tab === 'standings' && (
              <CompetitionStandingsTab
                key={standingsVersion}
                competitionId={competition.id}
                slug={competition.slug}
                discipline={competition.discipline}
                myUserId={user?.sub}
                categories={competition.categories}
                myCategoryId={competition.my_entry?.category_id ?? null}
                apiUrl={apiUrl}
                getAccessTokenSilently={getAccessTokenSilently}
              />
            )}
            {tab === 'scorecard' && (
              <CompetitionScorecardTab
                // Remounted with the standings, so a logged top shows here too.
                key={standingsVersion}
                competitionId={competition.id}
                categoryId={
                  competition.my_entry?.category_id ?? competition.categories[0]?.id ?? null
                }
                myUserId={user?.sub}
                apiUrl={apiUrl}
                getAccessTokenSilently={getAccessTokenSilently}
              />
            )}
            {tab === 'routes' && (
              <CompetitionRoutesTab
                competitionId={competition.id}
                gymId={gymId ?? ''}
                discipline={competition.discipline}
                scoringType={competition.scoring_type}
                userId={user?.sub ?? ''}
                apiUrl={apiUrl}
                // An competitor who paid at the desk for an open competition has
                // neither a subscription nor a check-in. Holding an open entry is
                // the right to log, which is what requireOpenEntry enforces
                // server-side; this only mirrors it.
                // Said in this competition's own terms: "you need an active
                // plan" is the gym Routes reason and is simply untrue here.
                cannotLogReason={
                  competition.awaiting_confirmation
                    ? t('competitions.cannotLogAwaiting')
                    : competition.my_session_state === 'ended'
                      ? t('competitions.cannotLogGroupEnded')
                      : competition.my_session_state === 'upcoming'
                        ? t('competitions.cannotLogGroupNotStarted', {
                            from: mySession ? formatWallClockTime(mySession.starts_at) : '',
                          })
                        : t('competitions.cannotLogNoAccess')
                }
                canLog={
                  competition.state === 'in_progress' &&
                  // Joining is registration; the desk decides admission.
                  !competition.awaiting_confirmation &&
                  // A time-slot group logs inside its own window only.
                  !groupClosed &&
                  (competition.join_policy === 'open' || hasActiveSubscription || isCheckedIn)
                }
                getAccessTokenSilently={getAccessTokenSilently}
                // Bump the standings key so the board is refetched on return.
                onLogged={() => setStandingsVersion((v) => v + 1)}
                // The page was left open past a cut-off: re-read, so the button
                // gives way to the reason.
                onRefused={() => void reload()}
              />
            )}
          </div>
        </div>
      ) : (
        <CompetitionIntro
          competition={competition}
          joining={joining}
          error={joinError}
          onJoin={(categoryId, sessionId) => void handleJoin(categoryId, sessionId)}
          onProfileMadePublic={async () => {
            setJoinError(null)
            await reload()
          }}
        />
      )}

      <GenderRequiredModal isOpen={genderModalOpen} onClose={() => setGenderModalOpen(false)} />
    </Page>
  )
}
